import {LockIcon, PinIcon} from '@primer/octicons-react'
import {Heading} from '@primer/react'
import type {ReactNode} from 'react'

import {LABELS} from '../../constants'
import {getIconColorClassName, getIconComponent, type SavedView} from '../../types'
import {viewVisibility} from '../../utils/views-visibility'
import type {PinControls} from '../index/PinViewMenuItem'
import {SavedViewActions} from './SavedViewActions'
import styles from './SavedViewHeader.module.css'

export function ViewHeaderIcon({icon: Icon, color}: {icon: ReturnType<typeof getIconComponent>; color: string}) {
  return <Icon size={16} className={`${styles.titleIcon} ${getIconColorClassName(color)}`} />
}

export function SavedViewStatusIcon({
  savedView,
  pinnedViewsEnabled,
  visibilityEnabled,
}: {
  savedView: SavedView
  pinnedViewsEnabled: boolean
  visibilityEnabled: boolean
}) {
  if (visibilityEnabled && viewVisibility(savedView) === 'personal') {
    return (
      <LockIcon
        size={16}
        aria-label={LABELS.views.privateIndicatorLabel}
        data-testid="view-private-indicator"
        className={styles.statusIcon}
      />
    )
  }
  if (pinnedViewsEnabled && savedView.pinned) {
    return (
      <PinIcon
        size={16}
        aria-label={LABELS.views.pinnedIndicatorLabel}
        data-testid="view-pinned-indicator"
        className={styles.statusIcon}
      />
    )
  }
  return null
}

interface SavedViewHeaderProps {
  savedView: SavedView
  owner: string
  repo: string
  viewsPath: string
  onStartEditing: () => void
  canManage: boolean
  pinnedViewsEnabled: boolean
  visibilityEnabled: boolean
  pinControls?: PinControls
  renderCreateIssueButton: () => ReactNode
  actionsPlacement?: 'end' | 'title'
}

export function SavedViewHeader({
  savedView,
  owner,
  repo,
  viewsPath,
  onStartEditing,
  canManage,
  pinnedViewsEnabled,
  visibilityEnabled,
  pinControls,
  renderCreateIssueButton,
  actionsPlacement = 'end',
}: SavedViewHeaderProps) {
  const actions = (
    <SavedViewActions
      savedView={savedView}
      owner={owner}
      repo={repo}
      viewsPath={viewsPath}
      onStartEditing={onStartEditing}
      canManage={canManage}
      pinControls={pinControls}
      renderCreateIssueButton={renderCreateIssueButton}
      compact={actionsPlacement === 'title'}
    />
  )

  return (
    <div className={styles.titleWrapper}>
      <div className={styles.header}>
        <div className={styles.titleArea}>
          <ViewHeaderIcon icon={getIconComponent(savedView.icon)} color={savedView.color} />
          <Heading as="h1" className={styles.title}>
            {savedView.name}
          </Heading>
          <SavedViewStatusIcon
            savedView={savedView}
            pinnedViewsEnabled={pinnedViewsEnabled}
            visibilityEnabled={visibilityEnabled}
          />
          {actionsPlacement === 'title' ? actions : null}
        </div>
        {actionsPlacement === 'end' ? actions : null}
      </div>
      {savedView.description && <p className={styles.description}>{savedView.description}</p>}
    </div>
  )
}
